import {
  IPartscareCheckoutSession,
  IPartscarePortalSession,
  IPartscareQuote,
  IPartscareSessionStatus,
} from '../model/IPartscare'

// --
// -- The partscare page is public, so it deliberately does NOT go through the
// -- shared RTK Query `api`: that base query redirects to /ordersignin on 401/404,
// -- which would bounce a customer who arrived from an emailed link.
// --

const API_HOST = process.env.REACT_APP_API_HOST || '/api/v1/'

const url = (path: string): string => `${API_HOST.replace(/\/$/, '')}/${path.replace(/^\//, '')}`

export class PartscareApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'PartscareApiError'
    this.status = status
  }
}

async function readError(response: Response): Promise<never> {
  let message = ''
  try {
    const body = await response.json()
    message = body?.error || body?.message || body?.errors?.[0] || ''
  } catch {
    // response had no JSON body
  }

  if (!message)
    message =
      response.status === 404
        ? 'We could not find that order or part.'
        : 'Something went wrong. Please try again.'

  throw new PartscareApiError(message, response.status)
}

export async function fetchPartscareQuote(
  orderId: string,
  productId: string,
  sig: string,
): Promise<IPartscareQuote> {
  const params = new URLSearchParams({ order_id: orderId, product_id: productId, sig })
  const response = await fetch(url(`partscare-public/quote?${params.toString()}`), {
    headers: { 'Sec-Fetch-Site': 'same-origin' },
  })
  if (!response.ok) return readError(response)
  return response.json()
}

export async function createPartscareCheckoutSession(
  orderId: string,
  orderItemId: number,
  sig: string,
  email?: string,
): Promise<IPartscareCheckoutSession> {
  const response = await fetch(url('partscare-public/checkout-session'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Sec-Fetch-Site': 'same-origin',
    },
    body: JSON.stringify({
      orderId,
      orderItemId,
      sig,
      ...(email ? { email } : {}),
    }),
  })
  if (!response.ok) return readError(response)
  return response.json()
}

export async function createPartscarePortalSession(
  orderId: string,
  orderItemId: number,
  sig: string,
): Promise<IPartscarePortalSession> {
  const response = await fetch(url('partscare-public/portal-session'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Sec-Fetch-Site': 'same-origin',
    },
    body: JSON.stringify({ orderId, orderItemId, sig }),
  })
  if (!response.ok) return readError(response)
  return response.json()
}

export async function fetchPartscareSessionStatus(
  orderId: string,
  sessionId: string,
  sig: string,
): Promise<IPartscareSessionStatus> {
  const params = new URLSearchParams({ order_id: orderId, session_id: sessionId, sig })
  const response = await fetch(url(`partscare-public/session-status?${params.toString()}`), {
    headers: { 'Sec-Fetch-Site': 'same-origin' },
  })
  if (!response.ok) return readError(response)
  return response.json()
}
